import { useEffect, useState, useMemo } from "react";
import { db } from "../../../src/firebaseConfig";
import { collection, getDocs } from "firebase/firestore";
import { motion } from "framer-motion";
import { 
  FiActivity, 
  FiAlertCircle, 
  FiCheckCircle, 
  FiXCircle, 
  FiRefreshCw, 
  FiTrendingUp,
  FiTrendingDown,
  FiPackage,
  FiDollarSign,
} from "react-icons/fi";

export function useProduct(applyRules) {
  const [products, setProducts] = useState([]);
  const [filteredProducts, setFilteredProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [selectedStatus, setSelectedStatus] = useState("all");
  const [searchQuery, setSearchQuery] = useState("");
  const [darkMode, setDarkMode] = useState(true);
  const [viewMode, setViewMode] = useState("cards");

  // Apply rules to all products
  const analyzedProducts = useMemo(() => {
    return products.map(product => {
      if (product.status && product.reason && product.action) {
        return {
          ...product,
          confidence: product.confidence || Math.round((product.confidenceValue || 0.7) * 100)
        };
      }
      
      const analysis = applyRules(product);
      return {
        ...product,
        status: analysis.status,
        reason: analysis.reason,
        action: analysis.action,
        confidence: Math.round(analysis.confidence * 100),
        originalStatus: product.status || "STABLE"
      };
    });
  }, [products]);
  
  // Initialize dark mode based on system preference or localStorage
  useEffect(() => {
    const savedTheme = localStorage.getItem("nexusflow-theme");
    const prefersDark = window.matchMedia("(prefers-color-scheme: dark)").matches;
    
    if (savedTheme === "light" || (!savedTheme && !prefersDark)) {
      setDarkMode(false);
    }
  }, []);

  // Toggle dark mode
  const toggleDarkMode = () => {
    setDarkMode(!darkMode);
    localStorage.setItem("nexusflow-theme", !darkMode ? "dark" : "light");
  };

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      try {
        const querySnapshot = await getDocs(
          collection(db, "analysis", "products", "items")
        );
        const data = querySnapshot.docs.map(doc => ({
          id: doc.id,
          ...doc.data()
        }));
        setProducts(data);
      } catch (error) {
        console.error("Error fetching data:", error);
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, []);

useEffect(() => {
  let filtered = analyzedProducts;

  if (selectedStatus !== "all") {
    filtered = filtered.filter(product => product.status === selectedStatus);
  }

  if (searchQuery) {
    const query = searchQuery.toLowerCase();

    filtered = filtered.filter(product => 
      (product.name?.toLowerCase().includes(query) || 
       product.reason?.toLowerCase().includes(query) || 
       product.action?.toLowerCase().includes(query))
    );
  }

  setFilteredProducts(filtered);
}, [selectedStatus, searchQuery, analyzedProducts]);


  const getStatusIcon = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "stable":
        return <FiCheckCircle className={darkMode ? "text-green-400" : "text-green-600"} />;
      case "warning":
      case "opportunity":
        return <FiAlertCircle className={darkMode ? "text-yellow-400" : "text-yellow-600"} />;
      case "critical":
        return <FiXCircle className={darkMode ? "text-red-400" : "text-red-600"} />;
      default:
        return <FiActivity className={darkMode ? "text-gray-400" : "text-gray-600"} />;
    }
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "active":
      case "stable":
        return darkMode ? "bg-green-500/20 text-green-400 border-green-500" : "bg-green-100 text-green-800 border-green-300";
      case "warning":
        return darkMode ? "bg-yellow-500/20 text-yellow-400 border-yellow-500" : "bg-yellow-100 text-yellow-800 border-yellow-300";
      case "opportunity":
        return darkMode ? "bg-blue-500/20 text-blue-400 border-blue-500" : "bg-blue-100 text-blue-800 border-blue-300";
      case "critical":
        return darkMode ? "bg-red-500/20 text-red-400 border-red-500" : "bg-red-100 text-red-800 border-red-300";
      default:
        return darkMode ? "bg-gray-500/20 text-gray-400 border-gray-500" : "bg-gray-100 text-gray-800 border-gray-300";
    }
  };

  const getConfidenceColor = (confidence) => {
    if (confidence >= 80) return darkMode ? "text-green-400" : "text-green-600";
    if (confidence >= 70) return darkMode ? "text-yellow-400" : "text-yellow-600";
    return darkMode ? "text-red-400" : "text-red-600";
  };

  const getProgressBarColor = (confidence) => {
    if (confidence >= 80) return darkMode ? "bg-green-500" : "bg-green-600";
    if (confidence >= 70) return darkMode ? "bg-yellow-500" : "bg-yellow-600";
    return darkMode ? "bg-red-500" : "bg-red-600";
  };

  const getSalesIndicator = (sales) => {
    if (sales > 30) return { icon: <FiTrendingUp />, color: darkMode ? "text-green-400" : "text-green-600", label: "High" };
    if (sales >= 10 && sales <= 30) return { icon: <FiActivity />, color: darkMode ? "text-yellow-400" : "text-yellow-600", label: "Medium" };
    return { icon: <FiTrendingDown />, color: darkMode ? "text-red-400" : "text-red-600", label: "Low" };
  };

  const getStockIndicator = (stock) => {
    if (stock > 50) return { icon: <FiPackage />, color: darkMode ? "text-red-400" : "text-red-600", label: "High" };
    if (stock >= 20 && stock <= 50) return { icon: <FiPackage />, color: darkMode ? "text-yellow-400" : "text-yellow-600", label: "Medium" };
    return { icon: <FiPackage />, color: darkMode ? "text-green-400" : "text-green-600", label: "Low" };
  };

  const getPriceIndicator = (price) => {
    if (price === "high") return { icon: <FiDollarSign />, color: darkMode ? "text-yellow-400" : "text-yellow-600", label: "High" };
    if (price === "medium") return { icon: <FiDollarSign />, color: darkMode ? "text-blue-400" : "text-blue-600", label: "Medium" };
    return { icon: <FiDollarSign />, color: darkMode ? "text-green-400" : "text-green-600", label: "Low" };
  };

  const containerVariants = {
    hidden: { opacity: 0 },
    visible: {
      opacity: 1,
      transition: {
        staggerChildren: 0.1
      }
    }
  };

  const itemVariants = {
    hidden: { y: 20, opacity: 0 },
    visible: {
      y: 0,
      opacity: 1,
      transition: {
        duration: 0.3,
        ease: "easeOut"
      }
    }
  };

  // Calculate stats
  const stats = useMemo(() => ({
    total: analyzedProducts.length,
    stable: analyzedProducts.filter(p => p.status === "STABLE").length,
    opportunity: analyzedProducts.filter(p => p.status === "OPPORTUNITY").length,
    critical: analyzedProducts.filter(p => p.status === "CRITICAL").length,
    warning: analyzedProducts.filter(p => p.status === "WARNING").length,
    averageConfidence: analyzedProducts.length > 0 
      ? Math.round(analyzedProducts.reduce((acc, p) => acc + p.confidence, 0) / analyzedProducts.length)
      : 0,
    totalSales: analyzedProducts.reduce((acc, p) => acc + (p.sales || 0), 0),
    totalStock: analyzedProducts.reduce((acc, p) => acc + (p.stock || 0), 0),
  }), [analyzedProducts]);

  // Theme classes
  const bgGradient = darkMode 
    ? "bg-gradient-to-br from-gray-900 via-gray-800 to-black" 
    : "bg-gradient-to-br from-gray-50 via-white to-blue-50";
  
  const textColor = darkMode ? "text-white" : "text-gray-900";
  const secondaryTextColor = darkMode ? "text-gray-400" : "text-gray-600";
  const cardBg = darkMode ? "bg-gray-800/50" : "bg-white/80";
  const cardBorder = darkMode ? "border-gray-700" : "border-gray-200";
  const inputBg = darkMode ? "bg-gray-700" : "bg-gray-100";
  const inputBorder = darkMode ? "border-gray-600" : "border-gray-300";
  const buttonBg = darkMode ? "bg-gray-700" : "bg-gray-200";
  const buttonHoverBg = darkMode ? "hover:bg-gray-600" : "hover:bg-gray-300";
  const footerBorder = darkMode ? "border-gray-800" : "border-gray-200";
  const tableHeaderBg = darkMode ? "bg-gray-800" : "bg-gray-100";
  const tableRowBg = darkMode ? "bg-gray-800/30" : "bg-white/50";
  const tableBorder = darkMode ? "border-gray-700" : "border-gray-200";

  if (isLoading) {
    return (
      <div className={`min-h-screen ${bgGradient} flex items-center justify-center`}>
        <motion.div
          animate={{ rotate: 360 }}
          transition={{ duration: 1, repeat: Infinity, ease: "linear" }}
          className={darkMode ? "text-blue-400 text-4xl" : "text-blue-600 text-4xl"}
        >
          <FiRefreshCw />
        </motion.div>
      </div>
    );
  }

  return {
    darkMode,
    toggleDarkMode,
    viewMode,
    setViewMode,
    filteredProducts,
    getStatusIcon,
    getStatusColor,
    getConfidenceColor,
    getProgressBarColor,
    getSalesIndicator,
    getStockIndicator,
    getPriceIndicator,
    containerVariants,
    itemVariants,
    stats,
    bgGradient,
    textColor,
    secondaryTextColor,
    cardBg,
    cardBorder,
    inputBg,
    inputBorder,
    buttonBg,
    buttonHoverBg,
    footerBorder,
    tableHeaderBg,
    tableRowBg,
    tableBorder,
    selectedStatus,
    setSelectedStatus,
    searchQuery,
    setSearchQuery,
    products,
    setProducts,
    isLoading,
    setIsLoading,
    analyzedProducts,
    setFilteredProducts,
    setDarkMode,
  }
}